import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import VaultModalCloseButton from "../../components/vault/VaultModalCloseButton";

const COPY = {
  en: {
    title: "Paid Ticket Participant Terms",
    effectiveDateLabel: "Effective date",
    effectiveDate: "13 June 2026",
    intro:
      "These Paid Ticket Participant Terms apply when you join and pay for a paid event organized by a verified third-party organizer through VaultGrid. They apply together with the User Agreement, Privacy Policy, platform rules, and the event-specific cancellation policy shown before payment.",
    sections: [
      {
        title: "1. Scope and platform role",
        paragraphs: [
          "These terms apply to host-paid events offered by verified organizers through VaultGrid.",
          "VaultGrid acts as the platform and intermediary for event discovery, booking flow, payment support, and participant communication. The organizer is the seller of the ticket and the provider of the event, except where mandatory law requires otherwise.",
        ],
      },
        {
          title: "2. Event information before payment",
          paragraphs: [
            "Before paying, you are responsible for reviewing the event description, date, time, location, organizer identity, ticket price, and the cancellation policy shown for the event.",
            "For paid host events, VaultGrid may show the organizer’s public identity, such as a business name or legal name, so you can understand who sells the ticket and provides the event. Private support details are not shown unless the organizer separately designates them as buyer-facing contacts.",
            "By continuing to checkout, you confirm that you reviewed that information and agree to proceed on that basis.",
          ],
        },
      {
        title: "3. Payment structure",
        paragraphs: [
          "A paid event may require either full ticket payment at checkout or a split payment structure with a deposit first and a remaining balance later.",
          "If a split payment applies, VaultGrid may show the amount due now, the remaining balance, and the deadline by which the balance must be paid.",
        ],
      },
      {
        title: "4. Remaining balance and unpaid reservations",
        paragraphs: [
          "If your ticket uses a deposit and a later balance payment, you are responsible for paying the remaining balance by the stated deadline.",
          "If the remaining balance is not paid on time, the organizer and/or VaultGrid may cancel your reserved place and release the spot for resale, subject to the event policy and any mandatory legal rights.",
        ],
      },
      {
        title: "5. Cancellation policy and refunds",
        paragraphs: [
          "You agree to the event-specific cancellation policy shown before payment. The same policy may apply to the deposit, the remaining balance, or both, depending on the event structure.",
          "Refund handling may depend on the event policy, the timing of your cancellation request, organizer-side changes, or mandatory legal rights that cannot be limited or excluded.",
        ],
      },
      {
        title: "6. Organizer responsibility",
        paragraphs: [
          "The organizer is responsible for organizing, operating, changing, postponing, cancelling, and delivering the event, as well as for venue compliance, permits, safety handling, and participant-facing fulfillment.",
          "The organizer is also responsible for the accuracy of organizer-provided event information and for any organizer-side customer support related to the event.",
        ],
      },
      {
        title: "7. Platform limitation of role",
        paragraphs: [
          "VaultGrid does not organize, host, control, or supervise the real-world conduct of the event itself.",
          "To the maximum extent permitted by law, VaultGrid is not responsible for event performance, venue issues, organizer misconduct, participant misconduct, injuries, property damage, alcohol-related incidents, or third-party claims arising from the event itself, except where liability follows from mandatory law and cannot be limited or excluded.",
        ],
      },
      {
        title: "8. Participant conduct and misuse",
        paragraphs: [
          "You must comply with platform rules, organizer instructions, and venue rules.",
          "Fraudulent claims, abusive chargebacks, false dispute reports, or deliberate misuse of paid event flows may result in account restrictions, participation restrictions, or further enforcement steps.",
        ],
      },
      {
        title: "9. Contact, records, and law",
        paragraphs: [
          "You agree that VaultGrid may send booking confirmations, payment notices, balance reminders, cancellation updates, and related event operations messages to the contact details connected to your account.",
          "These terms are governed by Slovak law, except where mandatory consumer protection law requires otherwise.",
        ],
      },
    ],
    backLabel: "Back",
    closeAria: "Close paid ticket participant terms",
  },
  ru: {
    title: "Условия для участника paid ticket event",
    effectiveDateLabel: "Дата вступления в силу",
    effectiveDate: "13 июня 2026",
    intro:
      "Эти условия применяются, когда вы присоединяетесь к платному событию проверенного стороннего организатора и оплачиваете билет через VaultGrid. Они действуют вместе с User Agreement, Privacy Policy, правилами платформы и cancellation policy конкретного события, показанной перед оплатой.",
    sections: [
      {
        title: "1. Сфера применения и роль платформы",
        paragraphs: [
          "Эти условия применяются к host-paid событиям, которые предлагаются проверенными организаторами через VaultGrid.",
          "VaultGrid выступает как платформа и посредник для discovery, booking flow, payment support и participant communication. Организатор является продавцом билета и исполнителем события, если обязательное право не требует иного.",
        ],
      },
        {
          title: "2. Информация о событии до оплаты",
          paragraphs: [
            "До оплаты вы обязаны самостоятельно ознакомиться с описанием события, датой, временем, локацией, личностью организатора, ценой билета и cancellation policy, показанной для этого события.",
            "Для paid host событий VaultGrid может показывать публичную идентичность организатора, например business name или legal name, чтобы вам было понятно, кто продаёт билет и кто проводит событие. Частные контактные данные поддержки не показываются, если только организатор отдельно не обозначил их как buyer-facing contacts.",
            "Продолжая checkout, вы подтверждаете, что ознакомились с этой информацией и согласны действовать на её основе.",
          ],
        },
      {
        title: "3. Структура оплаты",
        paragraphs: [
          "Для платного события может требоваться либо полная оплата билета сразу, либо split payment structure: сначала депозит, потом оставшаяся часть стоимости.",
          "Если применяется split payment, VaultGrid может показывать сумму к оплате сейчас, remaining balance и дедлайн, до которого нужно оплатить остаток.",
        ],
      },
      {
        title: "4. Оставшаяся сумма и неоплаченная бронь",
        paragraphs: [
          "Если билет оформляется через депозит и последующую доплату, вы обязаны оплатить remaining balance до указанного дедлайна.",
          "Если remaining balance не будет оплачена вовремя, организатор и/или VaultGrid могут отменить ваше забронированное место и освободить слот для перепродажи с учётом event policy и обязательных прав, которые нельзя ограничить.",
        ],
      },
      {
        title: "5. Cancellation policy и возвраты",
        paragraphs: [
          "Вы соглашаетесь с event-specific cancellation policy, показанной перед оплатой. Эта же политика может применяться к депозиту, к доплате остатка или к обоим платежам в зависимости от структуры события.",
          "Порядок возврата может зависеть от event policy, времени запроса на отмену, изменений со стороны организатора или обязательных прав, которые нельзя ограничить или исключить.",
        ],
      },
      {
        title: "6. Ответственность организатора",
        paragraphs: [
          "Организатор отвечает за проведение, изменение, перенос, отмену и фактическое исполнение события, а также за соответствие площадки, разрешения, безопасность и participant-facing fulfillment.",
          "Организатор также отвечает за точность предоставленной им информации о событии и за поддержку участников по вопросам самого события.",
        ],
      },
      {
        title: "7. Ограничение роли платформы",
        paragraphs: [
          "VaultGrid не организует, не проводит, не контролирует и не надзирает за офлайн-проведением события.",
          "В максимально допустимой законом степени VaultGrid не несёт ответственности за фактическое проведение события, проблемы площадки, misconduct со стороны организатора или участников, травмы, damage to property, alcohol-related incidents или third-party claims, возникающие из самого события, кроме случаев, когда ответственность следует из обязательного закона и не может быть ограничена или исключена.",
        ],
      },
      {
        title: "8. Поведение участника и злоупотребления",
        paragraphs: [
          "Вы обязаны соблюдать правила платформы, инструкции организатора и правила площадки.",
          "Fraudulent claims, abusive chargebacks, ложные dispute reports или иное злоупотребление paid event flow может привести к ограничениям аккаунта, ограничению участия или другим enforcement steps.",
        ],
      },
      {
        title: "9. Контакты, записи и право",
        paragraphs: [
          "Вы соглашаетесь, что VaultGrid может отправлять booking confirmations, payment notices, balance reminders, cancellation updates и другие операционные сообщения по контактным данным, связанным с вашим аккаунтом.",
          "Эти условия регулируются правом Словакии, кроме случаев, когда обязательное потребительское законодательство требует иного.",
        ],
      },
    ],
    backLabel: "Назад",
    closeAria: "Закрыть условия участника paid ticket event",
  },
  sk: {
    title: "Podmienky pre účastníkov platených podujatí",
    effectiveDateLabel: "Dátum účinnosti",
    effectiveDate: "13. júna 2026",
    intro:
      "Tieto podmienky sa uplatnia, keď sa pripájate na platené podujatie overeného externého organizátora a platíte lístok cez VaultGrid. Uplatňujú sa spolu s Podmienkami používania, Zásadami ochrany osobných údajov, pravidlami platformy a storno podmienkami konkrétneho podujatia zobrazenými pred platbou.",
    sections: [
      {
        title: "1. Rozsah a rola platformy",
        paragraphs: [
          "Tieto podmienky sa vzťahujú na platené podujatia ponúkané overenými organizátormi cez VaultGrid.",
          "VaultGrid vystupuje ako platforma a sprostredkovateľ pri vyhľadávaní podujatí, rezervácii, podpore platieb a komunikácii s účastníkmi. Organizátor je predávajúcim lístka a poskytovateľom podujatia, pokiaľ záväzné právne predpisy nevyžadujú inak.",
        ],
      },
        {
          title: "2. Informácie o podujatí pred platbou",
          paragraphs: [
            "Pred platbou ste zodpovedný za to, aby ste si skontrolovali opis podujatia, dátum, čas, miesto, identitu organizátora, cenu lístka a storno podmienky zobrazené pre dané podujatie.",
            "Pri platených podujatiach môže VaultGrid zobrazovať verejnú identitu organizátora, napríklad obchodné alebo zákonné meno, aby bolo zrejmé, kto lístok predáva a kto podujatie poskytuje. Súkromné kontaktné údaje podpory sa nezobrazujú, pokiaľ ich organizátor osobitne neurčí ako verejné kontakty pre kupujúcich.",
            "Pokračovaním k platbe potvrdzujete, že ste si tieto informácie prezreli a súhlasíte s pokračovaním na ich základe.",
          ],
        },
      {
        title: "3. Štruktúra platby",
        paragraphs: [
          "Platené podujatie môže vyžadovať buď plnú úhradu lístka pri platbe, alebo rozdelenú platbu: najprv depozit a potom zostávajúci doplatok.",
          "Ak sa používa rozdelená platba, VaultGrid môže zobrazovať sumu splatnú teraz, zostávajúcu sumu a termín, do ktorého musí byť doplatok uhradený.",
        ],
      },
      {
        title: "4. Zostávajúca suma a neuhradená rezervácia",
        paragraphs: [
          "Ak sa pri lístku používa depozit a neskorší doplatok, ste zodpovedný za úhradu zostávajúcej sumy do uvedeného termínu.",
          "Ak zostávajúca suma nebude uhradená načas, organizátor alebo VaultGrid môžu zrušiť vaše rezervované miesto a uvoľniť ho na ďalší predaj s prihliadnutím na podmienky podujatia a záväzné práva, ktoré nemožno obmedziť.",
        ],
      },
      {
        title: "5. Storno podmienky a vrátenie platieb",
        paragraphs: [
          "Súhlasíte so storno podmienkami konkrétneho podujatia zobrazenými pred platbou. Môžu sa vzťahovať na depozit, doplatok zostatku alebo na obe platby podľa štruktúry podujatia.",
          "Vrátenie platby môže závisieť od podmienok podujatia, času podania žiadosti o zrušenie, zmien na strane organizátora alebo záväzných práv, ktoré nemožno obmedziť ani vylúčiť.",
        ],
      },
      {
        title: "6. Zodpovednosť organizátora",
        paragraphs: [
          "Organizátor zodpovedá za organizáciu, priebeh, zmenu, presun, zrušenie a skutočné uskutočnenie podujatia, ako aj za súlad miesta konania s predpismi, povolenia, bezpečnosť a plnenie voči účastníkom.",
          "Organizátor je tiež zodpovedný za presnosť informácií o podujatí, ktoré poskytol, a za podporu účastníkov vo veciach súvisiacich so samotným podujatím.",
        ],
      },
      {
        title: "7. Obmedzenie roly platformy",
        paragraphs: [
          "VaultGrid podujatie neorganizuje, neusporadúva, nekontroluje ani nedohliada na jeho skutočný priebeh.",
          "V maximálnom rozsahu dovolenom zákonom VaultGrid nezodpovedá za samotný priebeh podujatia, problémy miesta konania, nevhodné správanie organizátora alebo účastníkov, zranenia, škody na majetku, incidenty súvisiace s alkoholom ani nároky tretích strán vyplývajúce zo samotného podujatia, okrem prípadov, keď zodpovednosť vyplýva zo záväzných právnych predpisov a nemožno ju obmedziť ani vylúčiť.",
        ],
      },
      {
        title: "8. Správanie účastníka a zneužitie",
        paragraphs: [
          "Musíte dodržiavať pravidlá platformy, pokyny organizátora a pravidlá miesta konania.",
          "Podvodné nároky, zneužívajúce spätné zúčtovania, nepravdivé hlásenia sporov alebo iné zneužitie platieb za podujatia môže viesť k obmedzeniu účtu, obmedzeniu účasti alebo ďalším opatreniam.",
        ],
      },
      {
        title: "9. Kontakt, záznamy a právo",
        paragraphs: [
          "Súhlasíte s tým, že VaultGrid môže zasielať potvrdenia rezervácie, oznámenia o platbe, pripomienky doplatku, informácie o zrušení a ďalšie prevádzkové správy na kontaktné údaje pripojené k vášmu účtu.",
          "Tieto podmienky sa riadia právom Slovenskej republiky, okrem prípadov, keď záväzné spotrebiteľské právo vyžaduje inak.",
        ],
      },
    ],
    backLabel: "Späť",
    closeAria: "Zavrieť podmienky pre účastníkov platených podujatí",
  },
};

function resolveLanguage(i18n, forcedLanguageCode) {
  if (forcedLanguageCode === "ru" || forcedLanguageCode === "sk" || forcedLanguageCode === "en") {
    return forcedLanguageCode;
  }
  const raw = String(i18n?.resolvedLanguage || i18n?.language || "en").toLowerCase();
  if (raw.startsWith("ru")) return "ru";
  if (raw.startsWith("sk") || raw.startsWith("cs")) return "sk";
  return "en";
}

const PaidTicketParticipantTermsPage = ({ forcedLanguageCode }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const language = useMemo(
    () => resolveLanguage(i18n, forcedLanguageCode),
    [forcedLanguageCode, i18n]
  );
  const copy = COPY[language] || COPY.en;
  const returnTo =
    location.state?.returnTo ||
    new URLSearchParams(location.search || "").get("returnTo") ||
    "/events/search";

  const handleBack = () => {
    navigate(returnTo);
  };

  return (
    <div
      className="min-h-screen text-white"
      style={{
        backgroundColor: "#091420",
        backgroundImage: "linear-gradient(180deg, #11233A 0%, #0D1D31 30%, #091420 100%)",
      }}
    >
      <div className="mx-auto max-w-4xl px-5 pb-16 pt-5 sm:px-6 lg:px-8">
        <div className="relative pr-12">
          <h1 className="text-[2rem] font-extrabold leading-[1.02] text-white sm:text-[2.3rem]">
            {copy.title}
          </h1>
          <div className="absolute right-0 top-0">
            <VaultModalCloseButton onClick={handleBack} aria-label={copy.closeAria} />
          </div>
        </div>

        <div className="mt-3 text-[0.92rem] leading-[1.5] text-white/64">
          {copy.effectiveDateLabel}: {copy.effectiveDate}
        </div>

        <div className="mt-6 rounded-[26px] border border-white/12 bg-white/[0.05] px-5 py-5 shadow-[0_20px_40px_rgba(0,0,0,0.24)] sm:px-6 sm:py-6">
          <p className="text-[0.98rem] leading-[1.7] text-white/84">
            {copy.intro}
          </p>

          <div className="mt-8 space-y-7">
            {copy.sections.map((section) => (
              <section key={section.title}>
                <h2 className="text-[1.08rem] font-extrabold leading-[1.35] text-white sm:text-[1.15rem]">
                  {section.title}
                </h2>
                <div className="mt-3 space-y-3">
                  {section.paragraphs.map((paragraph) => (
                    <p
                      key={paragraph}
                      className="text-[0.96rem] leading-[1.7] text-white/78"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex min-h-12 items-center justify-center rounded-[18px] border border-white/12 bg-white/[0.05] px-5 py-3 text-[0.96rem] font-semibold text-white/78"
            >
              {copy.backLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaidTicketParticipantTermsPage;
